import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Ship } from 'lucide-react';
import { COMPANY, IMAGES } from '../../data/site';
import { EASE } from './Reveal';

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '112%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Leaf = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 22c0-7 3.5-11.5 10.5-14C21.5 16 18 20.5 12 22z" />
    <path d="M12 22c0-5.5-2.8-9.4-8.5-11.5C4.5 17 7 20.6 12 22z" opacity="0.6" />
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const PARTICLES = [
  { top: '18%', left: '6%', size: 18, delay: 0, duration: 8, color: 'text-sage' },
  { top: '64%', left: '3%', size: 14, delay: 1.2, duration: 10, color: 'text-gold/60' },
  { top: '30%', left: '44%', size: 12, delay: 0.6, duration: 9, color: 'text-sage-light' },
  { top: '78%', left: '40%', size: 16, delay: 2, duration: 11, color: 'text-sage' },
  { top: '12%', left: '88%', size: 14, delay: 0.4, duration: 9, color: 'text-gold/50' },
  { top: '70%', left: '92%', size: 18, delay: 1.6, duration: 8, color: 'text-sage-light' },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 sm:pt-40 lg:pb-24">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-sage-mist blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-3xl" />

      {PARTICLES.map((p, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className={`pointer-events-none absolute hidden md:block ${p.color}`}
          style={{ top: p.top, left: p.left }}
          animate={{ y: [0, -16, 0], rotate: [0, 14, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Leaf className="" />
          <span style={{ width: p.size, height: p.size }} className="block" />
        </motion.span>
      ))}

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" />
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-forest">SKycodeX AgroTech — Pune, India</p>
          </motion.div>

          <h1 className="mt-6 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.45}>Quality Agricultural</MaskedLine>
            <MaskedLine delay={0.57}>Products, Prepared</MaskedLine>
            <MaskedLine delay={0.69}>
              for <span className="accent-italic font-semibold">Global Trade.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            India-based agricultural sourcing and export solutions focused on quality, transparency and reliable
            trade — currently supplying premium dehydrated onion flakes and powders.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#products"
              data-testid="hero-explore-products-btn"
              className="group inline-flex items-center gap-2.5 rounded-full bg-forest px-7 py-3.5 text-sm font-bold text-cream shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-deep hover:shadow-lift"
            >
              Explore Products
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#contact"
              data-testid="hero-send-enquiry-btn"
              className="group inline-flex items-center gap-2.5 rounded-full border border-forest/25 bg-white/60 px-7 py-3.5 text-sm font-bold text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-forest hover:bg-white hover:shadow-soft"
            >
              Send an Enquiry
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={COMPANY.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-2 text-sm font-bold text-forest link-underline"
            >
              <WhatsAppIcon className="h-4.5 w-4.5 text-forest" />
              WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-ink/50"
            data-testid="hero-route-line"
          >
            <Ship className="h-4 w-4 text-gold" />
            <span>From India</span>
            <motion.span
              className="inline-block h-1 w-1 rounded-full bg-gold"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span>to the World</span>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-4 -z-10 rounded-[3rem] bg-sage-mist" />
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-[2.5rem] shadow-lift">
              <motion.img
                src={IMAGES.heroMain}
                alt="Premium red onions sourced from Indian farms"
                data-testid="hero-image"
                initial={{ scale: 1.22 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.6, delay: 0.5, ease: EASE }}
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.3, ease: EASE }}
              className="absolute -left-4 bottom-10 sm:-left-8"
            >
              <div className="animate-floaty flex items-center gap-3 rounded-2xl border border-line bg-white/95 p-3 pr-5 shadow-lift backdrop-blur">
                <img
                  src={IMAGES.heroCard}
                  alt="Dehydrated onion flakes"
                  className="h-14 w-14 rounded-xl object-cover"
                  loading="lazy"
                />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Export Ready</p>
                  <p className="text-sm font-bold text-ink">Onion Flakes &amp; Powders</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.5, ease: EASE }}
              className="absolute -right-3 top-8 sm:-right-6"
            >
              <div className="rounded-full border border-line bg-cream/95 px-5 py-2.5 shadow-soft backdrop-blur">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-forest">
                  India <span className="text-gold">→</span> UAE <span className="text-gold">→</span> Beyond
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
